import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loginUser } from "../auth/authApi";

const API_URL = process.env.REACT_APP_API_URL;
const LOGIN_BROADCAST_KEY = 'samurai:login:broadcast';

const CANDIDATE_TOKEN_COOKIE_KEYS = ['token', 'accessToken', 'access_token', 'jwt', 'authToken'];

const decodeJwtPayload = (token) => {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
};

const getTokenFromCookies = () => {
  if (typeof document === 'undefined') return null;

  const cookieMap = Object.fromEntries(
    document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => {
        const separatorIndex = entry.indexOf('=');
        if (separatorIndex < 0) return [entry, ''];
        const key = entry.slice(0, separatorIndex);
        const value = entry.slice(separatorIndex + 1);
        return [key, decodeURIComponent(value)];
      }),
  );

  for (const key of CANDIDATE_TOKEN_COOKIE_KEYS) {
    const value = cookieMap[key];
    if (typeof value === 'string' && value.split('.').length === 3) {
      return value;
    }
  }

  const fallbackToken = Object.values(cookieMap).find(
    (value) => typeof value === 'string' && value.split('.').length === 3,
  );
  return fallbackToken || null;
};

const isTokenExpired = (token) => {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp) || exp <= 0) {
    return true;
  }
  return exp <= Math.floor(Date.now() / 1000);
};

const SignInLayer = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const redirectIfAuthenticated = async () => {
      const token = getTokenFromCookies();
      if (token && !isTokenExpired(token)) {
        if (mounted) {
          navigate('/home', { replace: true });
        }
        return;
      }

      try {
        // Cross-tab/session-cookie guard without axios interceptors:
        // avoid global 401 -> /sign-in redirects that can cause loops.
        await axios.get(`${API_URL}/user-profile`, {
          withCredentials: true,
        });
        if (mounted) {
          navigate('/home', { replace: true });
        }
      } catch (error) {
        // No active session for this browser context, stay on sign-in.
      }
    };

    redirectIfAuthenticated();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== LOGIN_BROADCAST_KEY || !event.newValue) {
        return;
      }
      window.location.reload();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSigningIn) return;
    setIsSigningIn(true);
    try {
      await loginUser({ email, password });
      // Clear any cached page permissions from a previous session
      localStorage.removeItem("userPages");
      localStorage.setItem(LOGIN_BROADCAST_KEY, String(Date.now()));
      // Redirect to a protected page (e.g., dashboard)
      navigate("/");
    } catch (error) {
      setErrorMessage("Invalid email or password.");
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <section className="auth bg-base d-flex flex-wrap">
      <div className="auth-left d-lg-block d-none">
        <div
          style={{
            height: '100vh',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <img
            src="assets/images/auth/auth-img.jpg"
            alt="Auth"
            style={{
              height: '100%',
              width: '100%',
              objectFit: 'cover',
              objectPosition: 'right',
              display: 'block',
            }}
          />
        </div>

      </div>
      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">

        <Link to="https://samurai-cmms.com" className="position-absolute top-0 end-0 m-4 pe-16" style={{ zIndex: 10 }}>
          <img src="assets/images/logo.png" alt="Samurai Logo" style={{ maxWidth: '250px' }} />
        </Link>
        <div className="max-w-464-px mx-auto w-100">
          <div>
            <h4 className="mb-12">Take Control of Your Maintenance</h4>
            <h5 className="mb-32 text-secondary-light text-lg fw-normal">
              Welcome back! Please enter your details.
            </h5>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="icon-field mb-16">
              <span className="icon top-50 translate-middle-y">
                <Icon icon="mage:email" />
              </span>
              <input
                type="email"
                className="form-control h-56-px bg-neutral-50 radius-12"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="position-relative mb-20">
              <div className="icon-field">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  id="your-password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    if (errorMessage) setErrorMessage("");
                    setPassword(e.target.value);
                  }}
                  required
                />
              </div>
              <span
                className={`toggle-password ri-eye-line cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 ${showPassword ? 'text-primary-600' : 'text-secondary-light'}`}
                data-toggle="#your-password"
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>
            {errorMessage && (
              <p className="text-danger mb-20">{errorMessage}</p>
            )}
            <div className="">
              <div className="d-flex justify-content-between gap-2">
                <div className="form-check style-check d-flex align-items-center">
                  <input
                    className="form-check-input border border-neutral-300"
                    type="checkbox"
                    id="remember"
                  />
                  <label className="form-check-label" htmlFor="remember">
                    Remember me
                  </label>
                </div>
                <Link to="/forgot-password" className="text-primary-600 fw-medium">
                  Forgot Password?
                </Link>
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
              disabled={isSigningIn}
            >
              {isSigningIn ? "Signing In..." : "Sign In"}
            </button>
            {/*<div className="mt-32 center-border-horizontal text-center">
              <span className="bg-base z-1 px-4">Or sign in with</span>
            </div>
            <div className="mt-32 d-flex align-items-center gap-3">
              <button
                type="button"
                className="fw-semibold text-primary-light py-16 px-24 w-50 border radius-12 text-md d-flex align-items-center justify-content-center gap-12 line-height-1 bg-hover-primary-50"
              >
                <Icon
                  icon="ic:baseline-facebook"
                  className="text-primary-600 text-xl line-height-1"
                />
                Facebook
              </button>
              <button
                type="button"
                className="fw-semibold text-primary-light py-16 px-24 w-50 border radius-12 text-md d-flex align-items-center justify-content-center gap-12 line-height-1 bg-hover-primary-50"
              >
                <Icon
                  icon="logos:google-icon"
                  className="text-primary-600 text-xl line-height-1"
                />
                Google
              </button>
            </div>*/}
            <div className="mt-32 text-center text-sm">
              <p className="mb-0">
                Don’t have an account?{" "}
                <Link to="/sign-up" className="text-primary-600 fw-semibold">
                  Sign Up
                </Link>
              </p>
              <p className="mt-16">
                <Link to="https://samurai-cmms.com" className="text-primary-600 fw-semibold">
                  Visit the Samurai Webiste
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SignInLayer;
